import Image from "next/image";
import {
  Box,
  Flex,
  IconButton,
  HStack,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  SCREENSHOT_HEIGHT,
  SCREENSHOT_WIDTH,
  SHOWCASE_AUTOPLAY_MS,
  SHOWCASE_SLIDE_GAP_PX,
  SYSTEM_SHOWCASE_SCREENS,
} from "./systemShowcaseScreens";

const SWIPE_THRESHOLD_PX = 48;

function parseSlideWidth(spec: string): number {
  if (spec.endsWith("vw")) {
    const vw = Number.parseFloat(spec);
    return (window.innerWidth * vw) / 100;
  }

  return Number.parseFloat(spec);
}

function getSlidePresentation(distance: number, isActive: boolean) {
  if (isActive) {
    return {
      scale: 1.03,
      opacity: 1,
      translateY: -4,
      zIndex: 2,
    };
  }

  if (distance === 1) {
    return {
      scale: 0.87,
      opacity: 0.7,
      translateY: 0,
      zIndex: 1,
    };
  }

  return {
    scale: 0.82,
    opacity: 0.5,
    translateY: 0,
    zIndex: 0,
  };
}

export function SystemShowcaseCarousel() {
  const slideWidthSpec =
    useBreakpointValue({
      base: "82vw",
      md: "280px",
      lg: "320px",
    }) ?? "320px";

  const controlsBelow = useBreakpointValue({ base: true, lg: false }) ?? false;
  const arrowSize = useBreakpointValue({ base: "sm", lg: "md" }) ?? "md";

  const [activeIndex, setActiveIndex] = useState(0);
  const [slideWidthPx, setSlideWidthPx] = useState(320);
  const [containerWidth, setContainerWidth] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const autoplayEpochRef = useRef(0);
  const dragStartXRef = useRef<number | null>(null);
  const dragDeltaRef = useRef(0);
  const isDraggingRef = useRef(false);

  const totalSlides = SYSTEM_SHOWCASE_SCREENS.length;
  const slideStep = slideWidthPx + SHOWCASE_SLIDE_GAP_PX;
  const trackOffset =
    containerWidth > 0
      ? containerWidth / 2 - slideWidthPx / 2 - activeIndex * slideStep
      : 0;

  const bumpAutoplayEpoch = useCallback(() => {
    autoplayEpochRef.current += 1;
  }, []);

  const goToSlide = useCallback(
    (index: number) => {
      bumpAutoplayEpoch();
      setActiveIndex(((index % totalSlides) + totalSlides) % totalSlides);
    },
    [bumpAutoplayEpoch, totalSlides],
  );

  const goNext = useCallback(() => {
    goToSlide(activeIndex + 1);
  }, [activeIndex, goToSlide]);

  const goPrev = useCallback(() => {
    goToSlide(activeIndex - 1);
  }, [activeIndex, goToSlide]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncPreference = () => setPrefersReducedMotion(media.matches);

    syncPreference();
    media.addEventListener("change", syncPreference);

    return () => media.removeEventListener("change", syncPreference);
  }, []);

  useEffect(() => {
    const updateSlideWidth = () => {
      setSlideWidthPx(parseSlideWidth(slideWidthSpec));
    };

    updateSlideWidth();
    window.addEventListener("resize", updateSlideWidth);

    return () => window.removeEventListener("resize", updateSlideWidth);
  }, [slideWidthSpec]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    observer.observe(node);
    setContainerWidth(node.clientWidth);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) return;

    const epochAtStart = autoplayEpochRef.current;

    const timer = window.setTimeout(() => {
      if (autoplayEpochRef.current !== epochAtStart) return;
      setActiveIndex((current) => (current + 1) % totalSlides);
      bumpAutoplayEpoch();
    }, SHOWCASE_AUTOPLAY_MS);

    return () => window.clearTimeout(timer);
  }, [
    activeIndex,
    bumpAutoplayEpoch,
    prefersReducedMotion,
    totalSlides,
  ]);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    bumpAutoplayEpoch();
    isDraggingRef.current = true;
    dragStartXRef.current = event.clientX;
    dragDeltaRef.current = 0;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current || dragStartXRef.current === null) return;
    dragDeltaRef.current = event.clientX - dragStartXRef.current;
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;

    isDraggingRef.current = false;
    event.currentTarget.releasePointerCapture(event.pointerId);

    if (dragDeltaRef.current <= -SWIPE_THRESHOLD_PX) {
      goNext();
    } else if (dragDeltaRef.current >= SWIPE_THRESHOLD_PX) {
      goPrev();
    }

    dragStartXRef.current = null;
    dragDeltaRef.current = 0;
  };

  const dotButtons = useMemo(
    () =>
      SYSTEM_SHOWCASE_SCREENS.map((_, index) => (
        <Box
          key={index}
          as="button"
          type="button"
          aria-label={`Ir para tela ${index + 1}`}
          aria-current={index === activeIndex ? "true" : undefined}
          onClick={() => goToSlide(index)}
          w={index === activeIndex ? "18px" : "8px"}
          h="8px"
          borderRadius="full"
          bg={index === activeIndex ? "orange.900" : "whiteAlpha.400"}
          transition="all 0.25s ease"
          _hover={{ bg: index === activeIndex ? "orange.900" : "whiteAlpha.600" }}
        />
      )),
    [activeIndex, goToSlide],
  );

  const arrowButtonProps = {
    variant: "ghost" as const,
    color: "white",
    border: "1px solid",
    borderColor: "whiteAlpha.300",
    bg: "blackAlpha.400",
    _hover: { bg: "blackAlpha.600", borderColor: "orange.900" },
  };

  return (
    <Box w="full">
      <Flex
        align="center"
        justify="center"
        gap={{ base: 0, lg: 4 }}
        w="full"
      >
        {!controlsBelow && (
          <IconButton
            {...arrowButtonProps}
            aria-label="Tela anterior"
            icon={<ChevronLeft size={22} />}
            size={arrowSize}
            flexShrink={0}
            onClick={goPrev}
          />
        )}

        <Box
          ref={containerRef}
          flex="1"
          maxW={{ base: "100%", lg: "980px" }}
          overflow="hidden"
          py={{ base: 4, md: 6 }}
          cursor="grab"
          _active={{ cursor: "grabbing" }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          userSelect="none"
          sx={{ touchAction: "pan-y" }}
        >
          <Flex
            align="flex-end"
            transform={`translateX(${trackOffset}px)`}
            transition={
              prefersReducedMotion
                ? "none"
                : "transform 0.55s cubic-bezier(0.22, 1, 0.36, 1)"
            }
            willChange="transform"
          >
            {SYSTEM_SHOWCASE_SCREENS.map((screen, index) => {
              const distance = Math.abs(index - activeIndex);
              const isActive = index === activeIndex;
              const presentation = getSlidePresentation(distance, isActive);

              return (
                <Box
                  key={screen.src}
                  flexShrink={0}
                  w={`${slideWidthPx}px`}
                  mr={
                    index === SYSTEM_SHOWCASE_SCREENS.length - 1
                      ? 0
                      : `${SHOWCASE_SLIDE_GAP_PX}px`
                  }
                  transform={`scale(${presentation.scale}) translateY(${presentation.translateY}px)`}
                  opacity={presentation.opacity}
                  zIndex={presentation.zIndex}
                  transition={
                    prefersReducedMotion
                      ? "none"
                      : "transform 0.55s ease, opacity 0.55s ease, box-shadow 0.55s ease"
                  }
                  onClick={() => {
                    if (index !== activeIndex) goToSlide(index);
                  }}
                  cursor={index === activeIndex ? "default" : "pointer"}
                >
                  <Box
                    borderRadius="28px"
                    border="1px solid"
                    borderColor="whiteAlpha.250"
                    bg="barber.900"
                    overflow="hidden"
                    boxShadow={
                      isActive
                        ? "0 28px 60px -16px rgba(251, 169, 49, 0.35), 0 16px 40px rgba(0, 0, 0, 0.45)"
                        : "0 12px 32px rgba(0, 0, 0, 0.35)"
                    }
                  >
                    <Image
                      src={screen.src}
                      alt={screen.alt}
                      width={SCREENSHOT_WIDTH}
                      height={SCREENSHOT_HEIGHT}
                      sizes="(max-width: 768px) 82vw, (max-width: 1024px) 280px, 320px"
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        objectFit: "contain",
                      }}
                      priority={index === 0}
                      loading={index === 0 ? undefined : "lazy"}
                      draggable={false}
                    />
                  </Box>
                </Box>
              );
            })}
          </Flex>
        </Box>

        {!controlsBelow && (
          <IconButton
            {...arrowButtonProps}
            aria-label="Próxima tela"
            icon={<ChevronRight size={22} />}
            size={arrowSize}
            flexShrink={0}
            onClick={goNext}
          />
        )}
      </Flex>

      <Flex
        mt={{ base: 2, lg: 6 }}
        direction={controlsBelow ? "column" : "row"}
        align="center"
        justify="center"
        gap={controlsBelow ? 4 : 3}
      >
        {controlsBelow && (
          <HStack spacing={3}>
            <IconButton
              {...arrowButtonProps}
              aria-label="Tela anterior"
              icon={<ChevronLeft size={18} />}
              size="sm"
              onClick={goPrev}
            />
            <IconButton
              {...arrowButtonProps}
              aria-label="Próxima tela"
              icon={<ChevronRight size={18} />}
              size="sm"
              onClick={goNext}
            />
          </HStack>
        )}

        <HStack spacing={2}>{dotButtons}</HStack>
      </Flex>
    </Box>
  );
}
